import React, { useState, useLayoutEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { Button } from 'antd';
import './SanitizedMarkdown.less';

type SanitizedMarkdownProps = {
  inputSrc?: string | null;
  maxLines?: number;
  isPrintView?: boolean;
};

const SanitizedMarkdown: React.FC<SanitizedMarkdownProps> = ({
  inputSrc,
  maxLines = 3,
  isPrintView = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current;
      const lineHeight = parseInt(
        window.getComputedStyle(element).lineHeight,
        10
      );
      const maxHeight = lineHeight * maxLines;

      // Force showToggle to true when expanded or in print view
      setShowToggle(isExpanded || element.scrollHeight > maxHeight);
    }
  }, [inputSrc, maxLines, isExpanded]);

  if (!inputSrc || typeof inputSrc !== 'string' || inputSrc.trim() === '') {
    return null;
  }

  try {
    const html = marked.parse(inputSrc);
    const sanitizedHtml =
      typeof html === 'string' ? DOMPurify.sanitize(html) : '';

    if (!sanitizedHtml.trim()) {
      return null;
    }

    return (
      <div className="sanitized-markdown-wrapper">
        <div
          ref={contentRef}
          className={`sanitized-markdown-content ${isExpanded || isPrintView ? 'expanded' : ''}`}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
        {showToggle && !isPrintView && (
          <Button
            type="link"
            className="toggle-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </div>
    );
  } catch (error) {
    console.error('Error parsing or sanitizing markdown:', error);
    return <div>Invalid content</div>;
  }
};

export default SanitizedMarkdown;
